/**
 * VideoCallRow Component
 *
 * Displays a video call meeting using the UniversalRow slot system.
 * Follows CallRow pattern with purple theme for video calls.
 */

import React, { useMemo, useState } from 'react';
import { Video } from 'lucide-react';
import {
  UniversalRow,
  FastActions,
  PrimaryAction,
  EntityAvatar,
  type FastActionType,
} from '../common/base';
import { LinkManager } from '../../services/actions/LinkManager';
import { getAvailableActions } from '../common/registry/ActionRegistry';
import { useTranslation } from '../../hooks';
import type { VideoCallMeeting, MeetingStatus } from '../../stores/videoCallStore';

// =============================================================================
// TYPES
// =============================================================================

export interface VideoCallEntity {
  id: string;
  title?: string;
  slug?: string;
  status?: MeetingStatus;
  scheduledAt?: string | null;
  createdAt?: string;
  participants?: string[];
  guestAccessEnabled?: boolean;
  contactName?: string;
}

export interface VideoCallRowProps {
  entity: VideoCallEntity;
  onTitleClick?: (entity: VideoCallEntity) => void;
  onActionClick?: (actionType: string, entityId: string) => void;
  onAvatarClick?: (name: string, id?: string) => void;
}

// =============================================================================
// COMPONENT
// =============================================================================

export function VideoCallRow({ entity, onTitleClick, onActionClick, onAvatarClick }: VideoCallRowProps) {
  const { t, language } = useTranslation();
  const locale = language === 'it' ? 'it-IT' : 'en-US';
  const [isSeen, setIsSeen] = useState(false);

  const linkedCounts = useMemo(() => {
    return LinkManager.getLinkedActionCounts(entity.id);
  }, [entity.id]);

  const availableActions = useMemo(() => {
    return getAvailableActions(entity.id) as FastActionType[];
  }, [entity.id]);

  // === SLOT: leftSlot ===
  const leftSlot = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <PrimaryAction
        type="eye"
        active={isSeen}
        onChange={(seen) => {
          setIsSeen(seen);
          onActionClick?.('seen', entity.id);
        }}
      />
      <EntityAvatar
        items={entity.contactName ? { id: entity.id, name: entity.contactName } : []}
        type="person"
        size={24}
        onClick={onAvatarClick ? (item) => onAvatarClick(item.name, item.id) : undefined}
      />
    </div>
  );

  // === SLOT: title ===
  const title = entity.title || t('videocall.title');

  // === SLOT: subtitle ===
  const subtitle = entity.participants && entity.participants.length > 0
    ? entity.participants.join(', ')
    : undefined;

  // === SLOT: icon ===
  const statusColor = entity.status === 'active' ? '#22c55e' : '#7C3AED';
  const icon = <Video size={16} color={statusColor} />;

  // === SLOT: meta ===
  const meta = useMemo(() => {
    if (entity.status === 'active') {
      return (
        <span style={{ color: '#22c55e', fontWeight: 500 }}>
          {t('videocall.meetingActive')}
        </span>
      );
    }
    if (entity.scheduledAt) {
      const date = new Date(entity.scheduledAt);
      return (
        <span style={{ color: '#6b7280' }}>
          {date.toLocaleString(locale, {
            day: '2-digit',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit',
          })}
        </span>
      );
    }
    return null;
  }, [entity.status, entity.scheduledAt, locale, t]);

  // === SLOT: date ===
  const date = entity.scheduledAt || entity.createdAt;

  // === SLOT: status ===
  const status = entity.status === 'ended' ? ('muted' as const) : undefined;

  // === SLOT: rightSlot ===
  const rightSlot = availableActions.length > 0 ? (
    <FastActions
      actions={availableActions}
      counts={linkedCounts as Partial<Record<FastActionType, number>>}
      onAction={(action) => onActionClick?.(action, entity.id)}
    />
  ) : null;

  return (
    <UniversalRow
      id={entity.id}
      leftSlot={leftSlot}
      title={title}
      subtitle={subtitle}
      icon={icon}
      meta={meta}
      date={date}
      status={status}
      done={isSeen || entity.status === 'ended'}
      rightSlot={rightSlot}
      onTitleClick={() => onTitleClick?.(entity)}
    />
  );
}
